/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, InputNumber, Button, Select, Card, Row, Col, message } from 'antd';
import { insurancePlanService, insuranceCompanyService } from '../../../services/insuranceService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const AddInsurancePlan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [form] = Form.useForm();
  const planId = searchParams.get('id');
  const preselectedCompanyId = searchParams.get('company_id');
  const isEdit = Boolean(planId);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await insuranceCompanyService.getAll({ per_page: 100, is_active: true });
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error('Error al cargar aseguradoras');
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (isEdit) {
      loadPlan();
    } else if (preselectedCompanyId) {
      form.setFieldsValue({ company_id: parseInt(preselectedCompanyId) });
    }
  }, [isEdit, planId, preselectedCompanyId]);

  const loadPlan = async () => {
    try {
      const plan = await insurancePlanService.getById(parseInt(planId));
      form.setFieldsValue({
        company_id: plan.company_id,
        name: plan.name,
        code: plan.code,
        coverage_percentage: plan.coverage_percentage,
        max_annual_amount: plan.max_annual_amount,
        description: plan.description
      });
    } catch (error) {
      message.error('Error al cargar plan');
      navigate('/insurance-plans');
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (isEdit) {
        await insurancePlanService.update(parseInt(planId), values);
        message.success('Plan actualizado correctamente');
      } else {
        await insurancePlanService.create(values);
        message.success('Plan creado correctamente');
      }
      navigate('/insurance-plans');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al guardar plan';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">{isEdit ? 'Editar' : 'Agregar'} Plan de Seguro</h3>
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Planes de Seguro', to: '/insurance-plans' }, { label: isEdit ? 'Editar' : 'Agregar' }]} />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <Card>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Aseguradora"
                        name="company_id"
                        rules={[{ required: true, message: 'La aseguradora es requerida' }]}
                      >
                        <Select
                          placeholder="Seleccione una aseguradora"
                          showSearch
                          optionFilterProp="children"
                        >
                          {companies.map(c => (
                            <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Nombre"
                        name="name"
                        rules={[{ required: true, message: 'El nombre es requerido' }]}
                      >
                        <Input placeholder="Nombre del plan" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Código"
                        name="code"
                        rules={[{ required: true, message: 'El código es requerido' }]}
                      >
                        <Input placeholder="Código único del plan" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Porcentaje de Cobertura (%)"
                        name="coverage_percentage"
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          placeholder="0-100"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Monto Máximo Anual"
                    name="max_annual_amount"
                  >
                    <InputNumber
                      min={0}
                      placeholder="Monto máximo anual"
                      formatter={value => value === undefined || value === null || value === '' ? '' : `RD$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/RD\$\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Descripción"
                    name="description"
                  >
                    <Input.TextArea rows={3} placeholder="Descripción adicional del plan" />
                  </Form.Item>

                  <Form.Item className="text-end">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      className="me-2"
                    >
                      {isEdit ? 'Actualizar' : 'Guardar'}
                    </Button>
                    <Button onClick={() => navigate('/insurance-plans')}>
                      Cancelar
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </div>
          </div>
    </>
  );
};

export default AddInsurancePlan;
